import { PiEyeglassesDuotone, PiNoteDuotone, PiRulerDuotone } from 'react-icons/pi';

import { Translate } from '@spazious/dollhouse-i18n';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStore } from '@spazious/dollhouse-store';
import { ViewModeEnum } from '@spazious/dollhouse-types';
import { ButtonProps, Stack, StackProps, Typography } from '@spazious/dollhouse-uikit';

import { useDeviceInfo } from '../../hooks/index.js';
import { Features } from '../Features/index.js';
import { useFeatures } from '../Features/useFeatures.js';
import { ToggleButtonLabel } from '../ToggleButtonLabel/index.js';
import { ToolModeButtonGroup } from '../ToolModeButtonGroup/index.js';

export interface ToolControlsProps extends StackProps {}

export const ToolControls: React.FC<ToolControlsProps> = (props) => {
  const set = useStore(({ set }) => set);
  const view = useStore(({ view }) => view);
  const { graph } = useGraph();
  const { hasFeature } = useFeatures();
  const isMobile = useDeviceInfo((state) => state.isMobile);
  const buttonSize: ButtonProps['size'] = isMobile ? 'small' : 'medium';
  const canToggleMeasurements = hasFeature('graphs.setup.node.measurement');
  const canToggleAnnotations = hasFeature('graphs.setup.node.annotation');
  const rulerToolEnabled = hasFeature('tool.ruler');
  const annotationToolEnabled = hasFeature('tool.annotation');
  const measurementsVisible = graph.layers.isEnabled(FShallowNodeTypesEnum.Measurement);
  const annotationsVisible = graph.layers.isEnabled(FShallowNodeTypesEnum.Annotation);

  // Do not use `graph.traverse` + `useMemo([graph])`: `graph` is a stable ref; children mount
  // asynchronously via the renderer, so the memo would stay stuck on the first (empty) traversal.
  // Shallow nodes mirror graph content and are not filtered by visibility layers.
  const shallowNodes = Object.values(graph.shallow.nodes);
  const hasMeasurementNodes = shallowNodes.some(
    (node) => node.type === FShallowNodeTypesEnum.Measurement
  );
  const hasAnnotationNodes = shallowNodes.some(
    (node) => node.type === FShallowNodeTypesEnum.Annotation
  );

  return (
    <Stack {...props}>
      <Features features="tool.view">
        <ToggleButtonLabel
          actived={view === ViewModeEnum.Outlines}
          label={
            <Typography size="small" variant="label">
              <Translate>View</Translate>
            </Typography>
          }
          size={buttonSize}
          onClick={() => {
            set(({ view }) => ({
              view: view === ViewModeEnum.Outlines ? ViewModeEnum.Normal : ViewModeEnum.Outlines,
            }));
          }}
        >
          <PiEyeglassesDuotone />
        </ToggleButtonLabel>
      </Features>
      {!rulerToolEnabled && hasMeasurementNodes && (
        <ToggleButtonLabel
          actived={measurementsVisible}
          disabled={!canToggleMeasurements}
          label={
            <Typography size="small" variant="label">
              <Translate>Measurements</Translate>
            </Typography>
          }
          size={buttonSize}
          onClick={() => graph.layers.toggle(FShallowNodeTypesEnum.Measurement)}
        >
          <PiRulerDuotone />
        </ToggleButtonLabel>
      )}
      {!annotationToolEnabled && hasAnnotationNodes && (
        <ToggleButtonLabel
          actived={annotationsVisible}
          disabled={!canToggleAnnotations}
          label={
            <Typography size="small" variant="label">
              <Translate>Annotations</Translate>
            </Typography>
          }
          size={buttonSize}
          onClick={() => graph.layers.toggle(FShallowNodeTypesEnum.Annotation)}
        >
          <PiNoteDuotone />
        </ToggleButtonLabel>
      )}
      <ToolModeButtonGroup />
    </Stack>
  );
};
